// 🆕 2026-09-01 · Nächster-Schritt-Panel (Prozess-Soll Punkt 2).
//
// Die Systemkarte hat gezeigt, wie Features sterben: als Knöpfe ohne
// Wann/Warum (field-fit: bis in die UI gebaut, 0 Nutzungen). Dieses
// Panel zeigt je Video die Statuskette und GENAU EINEN empfohlenen
// nächsten Schritt — der Nutzer muss die Reihenfolge nicht mehr wissen.
// Die Logik lebt komplett im Backend (modules/pipeline_status.py, 22
// Tests); hier wird nur gerendert.
import { useCallback, useEffect, useState } from "react";
import { fehlerText, getPipelineStatus, posenOhneKlickStarten } from "../../lib/api";

const ICON = {
  ok: "✓",
  offen: "○",
  laeuft: "⏳",
  fehler: "✗",
  hinweis: "❕",
  wartet: "·",
};
const FARBE = {
  ok: "text-emerald-400",
  offen: "text-amber-300",
  laeuft: "text-sky-300",
  fehler: "text-red-400",
  hinweis: "text-amber-200/80",
  wartet: "text-slate-600",
};

export default function PipelinePanel({ seqId, refreshKey = 0 }) {
  const [status, setStatus] = useState(null);
  const [fehler, setFehler] = useState(null);

  const laden = useCallback(() => {
    if (!seqId) return;
    getPipelineStatus(seqId)
      .then((d) => { setStatus(d); setFehler(null); })
      .catch((e) => setFehler(fehlerText(e, "Status nicht ladbar")));
  }, [seqId]);

  useEffect(() => { laden(); }, [laden, refreshKey]);

  // 🆕 2026-10-09 · Auftrag 40, R5 (S2-R10): der Schritt „Posen ohne Klick" kommt nur mit
  // dem Schalter S14_ERNTE_PRODUKTWEG aus dem Backend; ohne ihn bleibt das Panel wie zuvor.
  const pok = status?.schritte?.find((s) => s.key === "posen_ohne_klick");
  const [startFehler, setStartFehler] = useState(null);
  const [startet, setStartet] = useState(false);
  useEffect(() => {
    if (pok?.status !== "laeuft") return undefined;
    const t = setInterval(laden, 15000);
    return () => clearInterval(t);
  }, [pok?.status, laden]);
  const posenStarten = () => {
    setStartet(true);
    setStartFehler(null);
    posenOhneKlickStarten(seqId)
      .then(() => laden())
      .catch((e) => setStartFehler(fehlerText(e, "Start abgewiesen")))
      .finally(() => setStartet(false));
  };

  if (fehler) {
    return (
      <div data-testid="pipeline-panel-error"
           className="text-[10px] text-red-400/80 font-mono">
        Pipeline-Status: {fehler}
      </div>
    );
  }
  if (!status) return null;

  const next = status.next;
  return (
    <div
      data-testid="pipeline-panel"
      className="rounded border border-slate-700/60 bg-slate-900/40 px-2 py-1.5 space-y-1"
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
        {status.schritte.map((s) => (
          <span
            key={s.key}
            data-testid={`pipeline-step-${s.key}`}
            title={`${s.titel}${s.detail ? ` — ${s.detail}` : ""}`}
            className={`font-mono text-[10px] ${FARBE[s.status] || "text-slate-400"}`}
          >
            {ICON[s.status] || "?"} {s.titel}
          </span>
        ))}
      </div>
      {next ? (
        <div data-testid="pipeline-next"
             className="text-[11px] text-amber-200">
          ▶ <span className="font-semibold">{next.titel}:</span>{" "}
          {next.detail || "steht aus"}
        </div>
      ) : (
        <div data-testid="pipeline-done"
             className="text-[11px] text-emerald-300/90">
          ✓ Pipeline vollständig — nichts steht aus
          {status.schritte.some((s) => s.status === "hinweis")
            ? " (Hinweise beachten)" : ""}
        </div>
      )}
      {pok?.aktion === "posen_ohne_klick" ? (
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="posen-ohne-klick-start"
            disabled={startet}
            onClick={posenStarten}
            className="rounded border border-sky-600/60 px-2 py-0.5 text-[11px] text-sky-200 hover:bg-sky-900/40 disabled:opacity-50"
          >
            Posen ohne Klick rechnen
          </button>
          {startFehler ? (
            <span data-testid="posen-ohne-klick-fehler" className="text-[10px] text-red-400/80">
              {startFehler}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
